import React, { FC, HTMLAttributes, useEffect, useState } from "react";
import Header from "./header";
import ZeroScreen from "./zero-screen";
import MasterConfig, { ConfigRow } from "./master-config";
import LedgerConfig from "./ledger-config";
import GenerateModal from "./generate-modal";
import { COMPONENT_MODES, ComponentModeType } from "../../../data/constants";
import ConfirmationModal from "../../../components/reusable-ui/confirmation-modal";
import { useDispatch } from "react-redux";
import { fetchCommonData, savePermittedInventoryBranches } from "../../../redux/slices";
import { METHODS } from "../../../data/api-method-constants";
import { ENDPOINT } from "../../../services/apiUrls";
import PageLoader from "../../../components/page-loader";
import { XML_REPORT_TYPES_LIST } from "../../../data/accounting-constants";

interface StateType {
  createMapping: boolean;
  ledgerConfigMode: string;
  deleteConfirm: boolean;
  mappedBranches: string[];
  modalState: any;
  masterConfigRows: ConfigRow[];
  loading: boolean;
}

const XMLGenerator: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const [state, setState] = useState<StateType>({
    createMapping: false,
    modalState: { show: false, dateRange: undefined, branches: [''] },
    ledgerConfigMode: COMPONENT_MODES.viewMode,
    deleteConfirm: false,
    mappedBranches: [],
    masterConfigRows: [],
    loading: true,
  });
  const [selectedReportType, setSelectedReportType] = useState<string>(
    XML_REPORT_TYPES_LIST[0].value
  );
  const [masterConfigMode, setMasterConfigMode] = useState<ComponentModeType>(
    COMPONENT_MODES.viewMode
  );
  const [ledgerMapping, setLedgerMapping] = useState({});
  const dispatch = useDispatch();

  useEffect(() => {
    getPermittedInventoryBranches();
  }, []);

  useEffect(() => {
    getMasterConfig();
  }, [selectedReportType]);

  function toggleMapping() {
    setState({ ...state, createMapping: !state.createMapping });
  }

  function getPermittedInventoryBranches() {
    dispatch(
      fetchCommonData({
        url: ENDPOINT.BUSINESS.getInventoryBranches(),
        method: METHODS.POST,
        body: { onlyPermittedStores: true },
        action: savePermittedInventoryBranches,
      })
    );
  }

  function toggleGenerateModal() {
    setState({
      ...state,
      modalState: { ...state.modalState, show: !state.modalState.show },
    });
  }

  function toggleDelete() {
    setState({ ...state, deleteConfirm: !state.deleteConfirm });
  }

  function toggleEdit() {}

  function canGenerateXML() {
    return (
      state.ledgerConfigMode === COMPONENT_MODES.viewMode &&
      masterConfigMode === COMPONENT_MODES.viewMode &&
      Object.values(ledgerMapping).length > 0
    );
  }

  function isMappingCreated() {
    return (
      state?.mappedBranches?.length > 0 ||
      Object.values(ledgerMapping).length > 0
    );
  }

  function getMasterConfig() {
    dispatch(
      fetchCommonData({
        method: METHODS.GET,
        url: ENDPOINT.ACCOUNTING.masterConfig(selectedReportType),
        callback: handleMasterConfig,
      })
    );
  }

  function handleMasterConfig(response: any) {
    if (!response.code) {
      let newObjects: ConfigRow[] = [];
      response.forEach((val: any) => {
        newObjects.push({
          ...val,
          branchId: [val.branchId],
          labelInfo: transformDataFromArrToMap(val.labelInfo),
        });
      });
      let newMapped = response.map((val: any) => val.branchId);
      setState({
        ...state,
        masterConfigRows: newObjects,
        mappedBranches: newMapped,
        loading: false,
      });
    }
  }

  function renderXMLGenerator() {
    return (
      <>
        <div className="flex flex-col h-full bg-white m-20">
          <Header
            canGenerate={canGenerateXML()}
            toggleGenerateModal={toggleGenerateModal}
            mappedBranches={state.mappedBranches}
            selectedTab={selectedReportType}
            handleTabClick={setSelectedReportType}
          />
          <MasterConfig
            setMode={setMasterConfigMode}
            mode={masterConfigMode}
            getMasterConfig={getMasterConfig}
            configRows={state.masterConfigRows}
            selectedReportType={selectedReportType}
          />
          <LedgerConfig
            setMode={(val: any) =>
              setState({ ...state, ledgerConfigMode: val })
            }
            mode={state.ledgerConfigMode}
            setLedgerMapping={setLedgerMapping}
            selectedReportType={selectedReportType}
          />
        </div>
        <GenerateModal
          selectedReportType={selectedReportType}
          modalState={state.modalState}
          closeModal={toggleGenerateModal}
          mappedBranches={state.mappedBranches}
        />
        {state.deleteConfirm && (
          <ConfirmationModal
            headingText="Delete Mapping"
            description="Are you sure you want to delete Mapping? You wont be able to generate XML Files"
            onModalClose={toggleDelete}
            onConfirmBtnClick={toggleDelete}
          />
        )}
      </>
    );
  }

  return state.loading ? (
    <PageLoader show={true} />
  ) : isMappingCreated() || state.createMapping ? (
    renderXMLGenerator()
  ) : (
    <ZeroScreen toggleMapping={toggleMapping} />
  );
};
export default XMLGenerator;

// given an array with items as object in {key : string: value: string} form,
// this function converts the array to a map in [key] : [value] form
export function transformDataFromArrToMap(inputArr: { key: string; value: string }[] = []) {
  let result: Record<string, string> = {};
  for (let obj of inputArr) {
    result[obj.key] = obj.value;
  }
  return result;
}

// given a map in [key]: [value] form, this function converts the map to an array
// with items as object in {key : string: value: string} form
export function transformDataFromMapToArr(inputMap: Record<string, string> = {}) {
  let result: { key: string; value: string }[] = [];
  for (let key of Object.keys(inputMap)) {
    result.push({ key, value: inputMap[key] });
  }
  return result;
}
